/** Study-guide quiz banks for the Linear Algebra "Linear Equations" guide — 8 MCQs per section. */

export const LA_LE_FORMS_QUIZ = [
  {
    prompt: "Which is the general (standard) form of a linear equation in two variables?",
    options: ["$Ax+By=C$", "$Ax^2+By=C$", "$Axy=C$"],
    answer: "A",
    explanation: "Linear means degree 1 in every variable, with no products or powers of the unknowns.",
  },
  {
    prompt: "In $Ax+By=C$, what must be true for it to genuinely involve both variables?",
    options: ["$A$ and $B$ are not both zero", "$C\\neq 0$", "$A=B$"],
    answer: "A",
    explanation: "If both $A$ and $B$ were zero there would be no variables left in the equation.",
  },
  {
    prompt: "The general form of a linear equation in three variables is:",
    options: ["$Ax+By+Cz=D$", "$Ax+By=D$", "$ABCxyz=D$"],
    answer: "A",
    explanation: "One more variable, one more coefficient — the pattern just extends.",
  },
  {
    prompt: "The solution set of one linear equation $Ax+By+Cz=D$ (not all coefficients zero) is geometrically a:",
    options: ["Point", "Line", "Plane"],
    answer: "C",
    explanation: "A single linear equation in 3D removes exactly one degree of freedom, leaving a flat plane.",
  },
  {
    prompt: "The general form of a linear equation in $n$ variables, written compactly, is:",
    options: ["$a_1x_1+a_2x_2+\\cdots+a_nx_n=b$", "$x_1x_2\\cdots x_n=b$", "$a_1x_1^2+\\cdots+a_nx_n^2=b$"],
    answer: "A",
    explanation: "Every term is a constant times one variable, added together — that is the linear pattern.",
  },
  {
    prompt: "One linear equation in $n$ variables describes a:",
    options: ["Line, always", "Hyperplane in $\\mathbb{R}^n$", "Curve"],
    answer: "B",
    explanation: "It always cuts one dimension out of the space: a line in $\\mathbb{R}^2$, a plane in $\\mathbb{R}^3$, a hyperplane beyond that.",
  },
  {
    prompt: "Which of these is NOT a linear equation?",
    options: ["$3x-5y=10$", "$x+2y-z=0$", "$xy=4$"],
    answer: "C",
    explanation: "$xy$ is a product of two unknowns, which makes the equation nonlinear.",
  },
  {
    prompt: "Rewrite $5x-2y=10$ in slope-intercept form $y=mx+b$.",
    options: ["$y=\\tfrac{5}{2}x-5$", "$y=\\tfrac{2}{5}x-5$", "$y=-\\tfrac{5}{2}x+5$"],
    answer: "A",
    explanation: "$-2y=10-5x \\Rightarrow y=\\tfrac{5}{2}x-5$.",
  },

  {
    prompt: "Which equation is linear in $x$ and $y$?",
    options: ["$4x-3y=7$", "$x^2+y=7$", "$xy=7$"],
    answer: "A",
    explanation: "A linear equation contains variables only to the first power and no products of variables.",
  },
  {
    prompt: "In $7x-2y=9$, the coefficient of $y$ is:",
    options: ["$2$", "$-2$", "$9$"],
    answer: "B",
    explanation: "The coefficient is the signed number multiplying the variable.",
  },
  {
    prompt: "Which point satisfies $2x+y=7$?",
    options: ["$(1,3)$", "$(3,3)$", "$(2,3)$"],
    answer: "C",
    explanation: "Substitution gives $2(2)+3=7$.",
  },
  {
    prompt: "The equation $3x+4y-12=0$ written as $Ax+By=C$ is:",
    options: ["$3x+4y=12$", "$3x+4y=-12$", "$3x-4y=12$"],
    answer: "A",
    explanation: "Move the constant term to the right-hand side.",
  },
  {
    prompt: "A nontrivial linear equation in four variables describes a:",
    options: ["Single point", "Hyperplane in $\\mathbb{R}^4$", "Parabola"],
    answer: "B",
    explanation: "One independent linear equation removes one degree of freedom, leaving a hyperplane.",
  },
  {
    prompt: "Which expression can appear in a linear equation?",
    options: ["$z^2$", "$xz$", "$5z$"],
    answer: "C",
    explanation: "A linear term is a constant multiple of one variable.",
  },
  {
    prompt: "If $A=0$ in $Ax+By=C$ and $B\\neq0$, the graph is:",
    options: ["A horizontal line", "A vertical line", "A parabola"],
    answer: "A",
    explanation: "Then $By=C$, so $y=C/B$ is constant.",
  },
  {
    prompt: "If $B=0$ in $Ax+By=C$ and $A\\neq0$, the graph is:",
    options: ["A horizontal line", "A vertical line", "A circle"],
    answer: "B",
    explanation: "Then $Ax=C$, so $x=C/A$ is constant.",
  },
  {
    prompt: "Which equation is linear in $x,y,z$?",
    options: ["$x+y+z^2=1$", "$xy+z=1$", "$x-2y+5z=1$"],
    answer: "C",
    explanation: "Every variable appears only to the first power and is multiplied by a constant.",
  },
  {
    prompt: "For $a_1x_1+\\cdots+a_nx_n=b$, the vector of coefficients is commonly written:",
    options: ["$(a_1,\\ldots,a_n)$", "$(x_1,\\ldots,x_n)$", "$(b,b,\\ldots,b)$"],
    answer: "A",
    explanation: "The coefficients are the scalars multiplying the unknowns.",
  },
  {
    prompt: "The equation $0x+0y=5$ has:",
    options: ["Every point as a solution", "No solution", "Exactly one solution"],
    answer: "B",
    explanation: "It reduces to the false statement $0=5$.",
  },
  {
    prompt: "The equation $0x+0y=0$ has:",
    options: ["No solution", "Exactly one solution", "Every point in $\\mathbb{R}^2$ as a solution"],
    answer: "C",
    explanation: "It reduces to the identity $0=0$.",
  },
];

export const LA_LE_GRAPH_QUIZ = [
  {
    prompt: "To find the x-intercept of a line, you should:",
    options: ["Set $x=0$ and solve for $y$", "Set $y=0$ and solve for $x$", "Solve for both at once"],
    answer: "B",
    explanation: "The x-intercept is where the line crosses the x-axis — that's where $y=0$.",
  },
  {
    prompt: "To find the y-intercept of $Ax+By=C$, you should:",
    options: ["Set $y=0$", "Set $x=0$", "Set $A=0$"],
    answer: "B",
    explanation: "The y-intercept is where the line crosses the y-axis — that's where $x=0$.",
  },
  {
    prompt: "The x-intercept of $2x+3y=6$ is:",
    options: ["$(3,0)$", "$(0,2)$", "$(2,0)$"],
    answer: "A",
    explanation: "Set $y=0$: $2x=6 \\Rightarrow x=3$.",
  },
  {
    prompt: "The y-intercept of $2x+3y=6$ is:",
    options: ["$(0,3)$", "$(0,2)$", "$(2,0)$"],
    answer: "B",
    explanation: "Set $x=0$: $3y=6 \\Rightarrow y=2$.",
  },
  {
    prompt: "The line $x-y=4$ crosses the y-axis at:",
    options: ["$(0,-4)$", "$(0,4)$", "$(4,0)$"],
    answer: "A",
    explanation: "Set $x=0$: $-y=4 \\Rightarrow y=-4$.",
  },
  {
    prompt: "Once you have both intercepts of a line, the next step to graph it is:",
    options: ["Plot both points and draw a straight line through them", "Plot only the origin", "Compute a second derivative"],
    answer: "A",
    explanation: "Two points fully determine a straight line — connect them and extend in both directions.",
  },
  {
    prompt: "The equation $4x=20$ graphs as:",
    options: ["A horizontal line through $y=5$", "A vertical line through $x=5$", "A line through the origin"],
    answer: "B",
    explanation: "$4x=20 \\Rightarrow x=5$ for every $y$ — a vertical line.",
  },
  {
    prompt: "A vertical line such as $x=5$ has:",
    options: ["Only a y-intercept", "Only an x-intercept (unless it passes through the origin)", "Infinitely many x-intercepts"],
    answer: "B",
    explanation: "It never crosses the y-axis (unless the line is $x=0$), but it crosses the x-axis once.",
  },

  {
    prompt: "The slope of $y=-3x+5$ is:",
    options: ["$3$", "$-3$", "$5$"],
    answer: "B",
    explanation: "In slope-intercept form $y=mx+b$, the slope is $m$.",
  },
  {
    prompt: "The y-intercept of $y=2x-7$ is:",
    options: ["$(-7,0)$", "$(0,2)$", "$(0,-7)$"],
    answer: "C",
    explanation: "Set $x=0$ to obtain the y-intercept.",
  },
  {
    prompt: "The x-intercept of $3x+2y=12$ is:",
    options: ["$(4,0)$", "$(0,6)$", "$(6,0)$"],
    answer: "A",
    explanation: "Set $y=0$: $3x=12$, so $x=4$.",
  },
  {
    prompt: "A line parallel to $y=4x+1$ must have slope:",
    options: ["$-4$", "$4$", "$1/4$"],
    answer: "B",
    explanation: "Distinct parallel nonvertical lines have equal slopes.",
  },
  {
    prompt: "A line perpendicular to $y=2x+3$ has slope:",
    options: ["$2$", "$1/2$", "$-1/2$"],
    answer: "C",
    explanation: "Perpendicular nonvertical slopes are negative reciprocals.",
  },
  {
    prompt: "The line through $(0,2)$ and $(3,8)$ has slope:",
    options: ["$2$", "$3$", "$6$"],
    answer: "A",
    explanation: "$m=(8-2)/(3-0)=2$.",
  },
  {
    prompt: "Which equation represents a horizontal line?",
    options: ["$x=-4$", "$y=-4$", "$y=-4x$"],
    answer: "B",
    explanation: "A horizontal line has a constant y-coordinate.",
  },
  {
    prompt: "Which equation represents a vertical line?",
    options: ["$y=6$", "$y=6x$", "$x=6$"],
    answer: "C",
    explanation: "A vertical line has a constant x-coordinate.",
  },
  {
    prompt: "The slope of $2x+5y=10$ is:",
    options: ["$-2/5$", "$2/5$", "$-5/2$"],
    answer: "A",
    explanation: "Solve for $y$: $y=-(2/5)x+2$.",
  },
  {
    prompt: "The line with slope $3$ through $(1,2)$ is:",
    options: ["$y-1=2(x-3)$", "$y-2=3(x-1)$", "$y+2=3(x+1)$"],
    answer: "B",
    explanation: "Point-slope form is $y-y_1=m(x-x_1)$.",
  },
  {
    prompt: "If a line has x-intercept $4$ and y-intercept $2$, its intercept form is:",
    options: ["$x/2+y/4=1$", "$4x+2y=1$", "$x/4+y/2=1$"],
    answer: "C",
    explanation: "Intercept form is $x/a+y/b=1$.",
  },
  {
    prompt: "Two nonvertical lines with slopes $m_1$ and $m_2$ are perpendicular when:",
    options: ["$m_1m_2=-1$", "$m_1=m_2$", "$m_1+m_2=0$"],
    answer: "A",
    explanation: "Perpendicular slopes are negative reciprocals, so their product is $-1$.",
  },
];

export const LA_LE_SYS_QUIZ = [
  {
    prompt: "A system of linear equations can have:",
    options: ["Exactly 0, 1, or infinitely many solutions", "Exactly 0, 1, or 2 solutions", "Any number from 0 to 5 solutions"],
    answer: "A",
    explanation: "There is no fourth option — those are the only three possibilities for a linear system.",
  },
  {
    prompt: "Two lines that intersect at exactly one point give a system with:",
    options: ["No solution", "A unique solution", "Infinitely many solutions"],
    answer: "B",
    explanation: "One crossing point means one $(x,y)$ pair satisfies both equations.",
  },
  {
    prompt: "Two distinct parallel lines that never meet give a system with:",
    options: ["No solution", "A unique solution", "Infinitely many solutions"],
    answer: "A",
    explanation: "No shared point means no pair satisfies both equations — the system is inconsistent.",
  },
  {
    prompt: "Two equations that describe the very same line give a system with:",
    options: ["No solution", "A unique solution", "Infinitely many solutions (indefinite)"],
    answer: "C",
    explanation: "Every point on the line satisfies both equations — the system is dependent.",
  },
  {
    prompt: "The lines $y=3x+1$ and $y=3x-4$ are:",
    options: ["Parallel — no solution", "Intersecting — unique solution", "The same line"],
    answer: "A",
    explanation: "Same slope ($3$), different y-intercepts — parallel and never meeting.",
  },
  {
    prompt: "In standard form, $A_1x+B_1y=C_1$ and $A_2x+B_2y=C_2$ are parallel (or coincide) exactly when:",
    options: ["$A_1B_2=A_2B_1$", "$A_1=A_2$ only", "$C_1=C_2$"],
    answer: "A",
    explanation: "This cross-multiplication test compares slopes without dividing, so it also covers vertical lines.",
  },
  {
    prompt: "In a system with $n$ unknowns written as $A\\mathbf{x}=\\mathbf{b}$, the three possibilities (unique / none / infinite) still apply because:",
    options: [
      "Every linear system, in any number of variables, is inconsistent, has one solution, or has infinitely many",
      "Systems with more than 3 variables always have infinitely many solutions",
      "Only 2-variable systems can be inconsistent",
    ],
    answer: "A",
    explanation: "The three-possibility rule is a general fact about linear systems, not just 2D lines.",
  },
  {
    prompt: "A system is called \"indefinite\" (or dependent) when:",
    options: [
      "It has no solution",
      "It has infinitely many solutions described by one or more free parameters",
      "It has exactly one solution",
    ],
    answer: "B",
    explanation: "Indefinite systems leave at least one variable free, producing a whole family of solutions.",
  },

  {
    prompt: "The system $x+y=4$, $x-y=2$ has solution:",
    options: ["$(1,3)$", "$(2,2)$", "$(3,1)$"],
    answer: "C",
    explanation: "Adding the equations gives $2x=6$, so $x=3$ and then $y=1$.",
  },
  {
    prompt: "The system $x+y=2$, $2x+2y=4$ has:",
    options: ["Infinitely many solutions", "No solution", "Exactly one solution"],
    answer: "A",
    explanation: "The second equation is twice the first, so both describe the same line.",
  },
  {
    prompt: "The system $x+y=2$, $2x+2y=5$ has:",
    options: ["Infinitely many solutions", "No solution", "Exactly one solution"],
    answer: "B",
    explanation: "The left sides are proportional but the constants are not, so the lines are parallel and distinct.",
  },
  {
    prompt: "For a $2\\times2$ coefficient matrix $A$, $\\det(A)\\neq0$ guarantees:",
    options: ["No solution", "Infinitely many solutions", "A unique solution for every right-hand side"],
    answer: "C",
    explanation: "A nonzero determinant means $A$ is invertible.",
  },
  {
    prompt: "If a consistent system has at least one free variable, it has:",
    options: ["Infinitely many solutions", "No solution", "Exactly two solutions"],
    answer: "A",
    explanation: "Each free variable can vary continuously, generating infinitely many solutions.",
  },
  {
    prompt: "A system $A\\mathbf{x}=\\mathbf{b}$ is homogeneous when:",
    options: ["$A=0$", "$\\mathbf{b}=0$", "$\\mathbf{x}=0$ is forbidden"],
    answer: "B",
    explanation: "A homogeneous linear system has zero right-hand side.",
  },
  {
    prompt: "Every homogeneous linear system is:",
    options: ["Inconsistent", "Guaranteed to have only nonzero solutions", "Consistent"],
    answer: "C",
    explanation: "The zero vector always satisfies $A\\mathbf{x}=0$.",
  },
  {
    prompt: "A square system with an invertible coefficient matrix has:",
    options: ["Exactly one solution", "No solution", "Infinitely many solutions"],
    answer: "A",
    explanation: "Multiplying by $A^{-1}$ gives $\\mathbf{x}=A^{-1}\\mathbf{b}$.",
  },
  {
    prompt: "If two equations in two unknowns represent distinct parallel lines, the system is:",
    options: ["Dependent", "Inconsistent", "Uniquely solvable"],
    answer: "B",
    explanation: "Distinct parallel lines never intersect.",
  },
  {
    prompt: "If three planes in $\\mathbb{R}^3$ intersect in exactly one point, the system has:",
    options: ["No solution", "Infinitely many solutions", "A unique solution"],
    answer: "C",
    explanation: "The common intersection point is the single solution triple.",
  },
  {
    prompt: "In $A\\mathbf{x}=\\mathbf{b}$ with $A$ of size $m\\times n$, the vector $\\mathbf{x}$ has:",
    options: ["$n$ entries", "$m$ entries", "$m+n$ entries"],
    answer: "A",
    explanation: "The number of columns of $A$ equals the number of unknowns.",
  },
  {
    prompt: "In $A\\mathbf{x}=\\mathbf{b}$ with $A$ of size $m\\times n$, the vector $\\mathbf{b}$ has:",
    options: ["$n$ entries", "$m$ entries", "$mn$ entries"],
    answer: "B",
    explanation: "The number of rows of $A$ equals the number of equations and entries of $\\mathbf{b}$.",
  },
];

export const LA_LE_SOLVE_QUIZ = [
  {
    prompt: "Which of these is NOT one of the three elementary operations used to solve a system?",
    options: ["Interchange two equations", "Multiply an equation by a nonzero constant", "Square both sides of an equation"],
    answer: "C",
    explanation: "Squaring can introduce new, false solutions — it is not a safe elementary operation.",
  },
  {
    prompt: "Why is it legal to multiply an equation by a nonzero constant?",
    options: [
      "It scales both sides equally, so the solution set stays exactly the same",
      "It changes the solution set on purpose",
      "It only works for equations that already have a solution",
    ],
    answer: "A",
    explanation: "Multiplying both sides by the same nonzero number preserves every equality.",
  },
  {
    prompt: "Why can't you multiply an equation by zero as a solving technique?",
    options: [
      "It turns the equation into $0=0$, destroying all information about the unknowns",
      "It is perfectly fine to do",
      "It only changes the sign of every term",
    ],
    answer: "A",
    explanation: "Zero collapses the equation to a trivial statement that is true for every value — you lose the constraint.",
  },
  {
    prompt: "Adding a multiple of one equation to another is useful because it can:",
    options: ["Change the number of unknowns", "Eliminate a variable, revealing another one's value", "Force the system to have no solution"],
    answer: "B",
    explanation: "This is the core elimination move: combine equations so one unknown cancels out.",
  },
  {
    prompt: "For $x+y+z=6,\\ 2x-y+z=3,\\ x+2y-z=2$: eliminating $x$ from the second equation with $E_2-2E_1$ gives:",
    options: ["$-3y-z=-9$", "$3y+z=3$", "$y+z=-3$"],
    answer: "A",
    explanation: "$(2x-y+z)-2(x+y+z)=3-12 \\Rightarrow -3y-z=-9$.",
  },
  {
    prompt: "Continuing that system, eliminating $x$ from the third equation with $E_3-E_1$ gives:",
    options: ["$y-2z=-4$", "$2y-z=-4$", "$y+2z=4$"],
    answer: "A",
    explanation: "$(x+2y-z)-(x+y+z)=2-6 \\Rightarrow y-2z=-4$.",
  },
  {
    prompt: "Solving the reduced pair $3y+z=9$ and $y-2z=-4$ gives $z=3,\\ y=2$. Back-substituting into $x+y+z=6$ gives:",
    options: ["$x=1$", "$x=3$", "$x=6$"],
    answer: "A",
    explanation: "$x+2+3=6 \\Rightarrow x=1$.",
  },
  {
    prompt: "So the full solution to $x+y+z=6,\\ 2x-y+z=3,\\ x+2y-z=2$ is:",
    options: ["$(x,y,z)=(1,2,3)$", "$(x,y,z)=(3,2,1)$", "$(x,y,z)=(2,1,3)$"],
    answer: "A",
    explanation: "Check: $1+2+3=6$, $2(1)-2+3=3$, $1+2(2)-3=2$ — all three equations hold.",
  },

  {
    prompt: "Using substitution on $x+y=5$ and $x-y=1$, the solution is:",
    options: ["$(3,2)$", "$(2,3)$", "$(4,1)$"],
    answer: "A",
    explanation: "Adding the equations gives $2x=6$, hence $x=3$ and $y=2$.",
  },
  {
    prompt: "Eliminating $y$ from $2x+y=7$ and $x-y=2$ gives:",
    options: ["$x=9$", "$3x=9$", "$3y=9$"],
    answer: "B",
    explanation: "Adding the equations cancels $y$ and gives $3x=9$.",
  },
  {
    prompt: "The solution of $2x+y=8$ and $x+y=5$ is:",
    options: ["$(2,3)$", "$(4,1)$", "$(3,2)$"],
    answer: "C",
    explanation: "Subtract the second equation from the first: $x=3$, then $y=2$.",
  },
  {
    prompt: "If a row operation produces $0=4$, the system is:",
    options: ["Inconsistent", "Dependent", "Guaranteed unique"],
    answer: "A",
    explanation: "A false equation signals that no vector can satisfy all equations.",
  },
  {
    prompt: "If row reduction produces a zero row $0=0$, that row:",
    options: ["Makes the system inconsistent", "Adds no new constraint", "Forces every variable to zero"],
    answer: "B",
    explanation: "The identity $0=0$ is redundant.",
  },
  {
    prompt: "When solving by elimination, multiplying a row by $-2$ is:",
    options: ["Invalid because the factor is negative", "Valid only for homogeneous systems", "A valid elementary row operation"],
    answer: "C",
    explanation: "Any nonzero scalar multiple of a row preserves the solution set.",
  },
  {
    prompt: "The solution of $x+2y=7$ and $3x-2y=5$ is:",
    options: ["$(3,2)$", "$(2,3)$", "$(4,1)$"],
    answer: "A",
    explanation: "Adding gives $4x=12$, so $x=3$ and then $y=2$.",
  },
  {
    prompt: "To eliminate $x$ from $x+y=4$ and $2x-y=5$, a useful operation is:",
    options: ["$R_2\\leftarrow 2R_2$", "$R_2\\leftarrow R_2-2R_1$", "$R_1\\leftarrow R_1+R_2$"],
    answer: "B",
    explanation: "Subtracting twice the first equation makes the x-coefficient in the second row zero.",
  },
  {
    prompt: "Back-substitution is normally performed after obtaining:",
    options: ["A determinant only", "A graph only", "Row echelon form"],
    answer: "C",
    explanation: "An echelon system can be solved from the last pivot equation upward.",
  },
  {
    prompt: "Gauss–Jordan elimination continues Gaussian elimination until the matrix is in:",
    options: ["Reduced row echelon form", "Diagonal form only for every matrix", "Upper triangular form with arbitrary pivot values"],
    answer: "A",
    explanation: "Gauss–Jordan clears entries above and below pivots and scales pivots to $1$.",
  },
  {
    prompt: "The solution of $3x=12$ is:",
    options: ["$x=3$", "$x=4$", "$x=36$"],
    answer: "B",
    explanation: "Divide both sides by the nonzero coefficient $3$.",
  },
  {
    prompt: "Why must a row-scaling factor be nonzero?",
    options: ["Negative factors are forbidden", "Fractions are forbidden", "Scaling by zero would destroy information and is not reversible"],
    answer: "C",
    explanation: "Elementary row operations must be reversible to preserve the solution set.",
  },
];
